import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inputs, DEFAULT_INPUTS, IndustryType } from '../utils/roiCalculations';
import { fmtMoney } from '../utils/formatters';
type QuestionId = 'dealSize' | 'margin' | 'rookieSales' | 'vetSales' | 'closeRate' | 'contact' | 'preview';
interface Question {
  id: QuestionId;
  question: string;
  subtitle?: string;
  field: keyof Inputs;
  type: 'range' | 'contact';
  min?: number;
  max?: number;
  step?: number;
  formatValue?: (value: number) => string;
}
const questions: Question[] = [{
  id: 'dealSize',
  question: "What's your average deal size?",
  subtitle: 'Revenue per sale',
  field: 'ticket',
  type: 'range',
  min: 10000,
  max: 100000,
  step: 1000,
  formatValue: v => fmtMoney(v)
}, {
  id: 'margin',
  question: "What's your gross profit margin?",
  subtitle: "Percentage of revenue that's profit",
  field: 'gpm',
  type: 'range',
  min: 5,
  max: 75,
  step: 5,
  formatValue: v => `${v}%`
}, {
  id: 'rookieSales',
  question: 'How many sales does a new rep make in their first 90 days?',
  subtitle: 'Total sales over the first 3 months',
  field: 'salesFirst90Total',
  type: 'range',
  min: 5,
  max: 25,
  step: 1,
  formatValue: v => `${v} sales`
}, {
  id: 'vetSales',
  question: 'How many sales per month does a veteran rep make?',
  subtitle: 'Monthly sales for experienced reps (90+ days)',
  field: 'salesVetMonthly',
  type: 'range',
  min: 3,
  max: 20,
  step: 1,
  formatValue: v => `${v} sales/month`
}, {
  id: 'closeRate',
  question: 'What percentage of sales with contingencies do you expect to get installed?',
  subtitle: 'Your typical installation rate',
  field: 'pullthrough',
  type: 'range',
  min: 40,
  max: 100,
  step: 5,
  formatValue: v => `${v}%`
}, {
  id: 'contact',
  question: 'Where should we send your results?',
  subtitle: 'Optional - or skip to see your results now',
  field: 'email',
  type: 'contact'
}];
export function CalculatorFlow() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [inputs, setInputs] = useState<Inputs>({
    ...DEFAULT_INPUTS,
    industry: 'Roofing',
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [showPreview, setShowPreview] = useState(false);
  const currentQuestion = questions[currentStep];
  const progress = (currentStep + 1) / questions.length * 100;
  const handleAnswer = (field: keyof Inputs, value: any) => {
    const newInputs = {
      ...inputs,
      [field]: value
    };
    setInputs(newInputs);
    if (currentStep < questions.length - 1) {
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
      }, 300);
    } else if (currentQuestion.id === 'contact') {
      // If they filled out contact info, show preview
      if (newInputs.email && newInputs.companyName && newInputs.firstName && newInputs.lastName && newInputs.phone) {
        setShowPreview(true);
      } else {
        // Otherwise go directly to results
        handleSubmit();
      }
    }
  };
  const handleSliderChange = (field: keyof Inputs, value: number) => {
    setInputs({
      ...inputs,
      [field]: value
    });
  };
  const handleBack = () => {
    if (showPreview) {
      setShowPreview(false);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/');
    }
  };
  const handleSubmit = () => {
    localStorage.setItem('calculatorInputs', JSON.stringify(inputs));
    navigate('/results');
  };
  const handleSkipToResults = () => {
    handleSubmit();
  };
  const estimatedHires = Math.round(inputs.adSpend * 1 / inputs.cph);
  const estimatedRevenue = estimatedHires * 0.7 * inputs.salesFirst90Total * inputs.ticket * (inputs.pullthrough / 100);
  const totalCost = inputs.adSpend + estimatedHires * 5 * inputs.recruiterHourlyRate;
  const estimatedProfit = estimatedRevenue * (inputs.gpm / 100) - totalCost;
  if (showPreview) {
    return <div className="min-h-screen bg-gradient-to-br from-navy via-navy-light to-navy flex flex-col" data-id="element-439">
        <div className="flex-1 flex items-center justify-center px-4 py-6 md:py-8" data-id="element-440">
          <div className="max-w-2xl w-full" data-id="element-441">
            <div className="flex justify-center mb-8" data-id="element-442">
              <img src="https://cdn.magicpatterns.com/uploads/jrZpZA6KS7cefrnJNRcqNs/Copy_of_HIRE.png" alt="HIRE" className="h-8 md:h-10" data-id="element-443" />
            </div>

            <div className="text-center mb-6 md:mb-8" data-id="element-444">
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-3 md:mb-4" data-id="element-445">
                Here's Your Quick Preview
              </h2>
              <p className="text-base md:text-lg text-blue-100" data-id="element-446">
                Based on your inputs, here's what you can expect
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 md:p-8 border-2 border-white/20 mb-6 md:mb-8" data-id="element-447">
              <div className="grid grid-cols-2 gap-4 md:gap-6" data-id="element-448">
                <div className="text-center" data-id="element-449">
                  <div className="text-blue-200 text-xs md:text-sm font-semibold mb-1 md:mb-2" data-id="element-450">
                    Estimated Monthly Hires
                  </div>
                  <div className="text-2xl md:text-4xl font-bold text-white" data-id="element-451">
                    {estimatedHires}
                  </div>
                </div>
                <div className="text-center" data-id="element-452">
                  <div className="text-blue-200 text-xs md:text-sm font-semibold mb-1 md:mb-2" data-id="element-453">
                    Monthly Investment
                  </div>
                  <div className="text-2xl md:text-4xl font-bold text-amber" data-id="element-454">
                    {fmtMoney(totalCost)}
                  </div>
                </div>
                <div className="text-center" data-id="element-455">
                  <div className="text-blue-200 text-xs md:text-sm font-semibold mb-1 md:mb-2" data-id="element-456">
                    Projected Monthly Revenue
                  </div>
                  <div className="text-2xl md:text-4xl font-bold text-green-400" data-id="element-457">
                    {fmtMoney(estimatedRevenue)}
                  </div>
                </div>
                <div className="text-center" data-id="element-458">
                  <div className="text-blue-200 text-xs md:text-sm font-semibold mb-1 md:mb-2" data-id="element-459">
                    Estimated Monthly Profit
                  </div>
                  <div className={`text-2xl md:text-4xl font-bold ${estimatedProfit >= 0 ? 'text-green-400' : 'text-red-400'}`} data-id="element-460">
                    {fmtMoney(estimatedProfit)}
                  </div>
                </div>
              </div>
            </div>

            <button onClick={handleSubmit} className="w-full py-4 md:py-5 bg-amber hover:bg-amber-dark text-navy font-bold text-lg md:text-xl rounded-lg transition-all shadow-2xl hover:shadow-amber/50 hover:scale-105 active:scale-100" data-id="element-461">
              See My Full 12-Month Projection →
            </button>

            {inputs.email && <p className="text-center text-blue-200 text-xs md:text-sm mt-3 md:mt-4" data-id="element-462">
                We'll email your detailed results to{' '}
                <span className="font-semibold text-white" data-id="element-463">{inputs.email}</span>
              </p>}

            <button onClick={handleBack} className="flex items-center gap-1.5 text-blue-200 hover:text-white transition-colors mx-auto mt-6" data-id="element-464">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-465">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" data-id="element-466" />
              </svg>
              <span className="text-sm font-semibold" data-id="element-467">Back</span>
            </button>
          </div>
        </div>

        <footer className="bg-navy/50 border-t-2 border-white/10" data-id="element-468">
          <div className="max-w-7xl mx-auto px-4 py-4 md:py-6" data-id="element-469">
            <div className="flex flex-col md:flex-row items-center justify-between gap-3" data-id="element-470">
              <img src="https://cdn.magicpatterns.com/uploads/jrZpZA6KS7cefrnJNRcqNs/Copy_of_HIRE.png" alt="HIRE" className="h-6 md:h-8" data-id="element-471" />
              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-xs md:text-sm text-blue-200" data-id="element-472">
                <button onClick={() => navigate('/')} className="hover:text-white transition-colors" data-id="element-473">
                  Home
                </button>
              </div>
            </div>
          </div>
        </footer>
      </div>;
  }
  return <div className="min-h-screen bg-gradient-to-br from-navy via-navy-light to-navy flex flex-col" data-id="element-474">
      <div className="flex-1 flex items-center justify-center px-4 py-6 md:py-8" data-id="element-475">
        <div className="max-w-2xl w-full" data-id="element-476">
          <div className="flex justify-center mb-6 md:mb-8" data-id="element-477">
            <img src="https://cdn.magicpatterns.com/uploads/jrZpZA6KS7cefrnJNRcqNs/Copy_of_HIRE.png" alt="HIRE" className="h-8 md:h-10" data-id="element-478" />
          </div>

          {currentStep > 0 && <button onClick={handleBack} className="flex items-center gap-1.5 text-blue-200 hover:text-white transition-colors mb-4" data-id="element-479">
              <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-480">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" data-id="element-481" />
              </svg>
              <span className="text-xs md:text-sm font-semibold" data-id="element-482">Back</span>
            </button>}

          <div className="mb-6 md:mb-8" data-id="element-483">
            <div className="h-1.5 md:h-2 bg-white/20 rounded-full overflow-hidden" data-id="element-484">
              <div className="h-full bg-amber transition-all duration-300 ease-out" style={{
              width: `${progress}%`
            }} data-id="element-485" />
            </div>
            <div className="flex justify-between mt-1.5 md:mt-2 text-[10px] md:text-xs text-blue-200" data-id="element-486">
              <span data-id="element-487">
                Question {currentStep + 1} of {questions.length}
              </span>
              <span data-id="element-488">{Math.round(progress)}% complete</span>
            </div>
          </div>

          <div className="animate-fade-in" data-id="element-489">
            <div className="mb-6 md:mb-8" data-id="element-490">
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-2 md:mb-3" data-id="element-491">
                {currentQuestion.question}
              </h2>
              {currentQuestion.subtitle && <p className="text-sm md:text-lg text-blue-100" data-id="element-492">
                  {currentQuestion.subtitle}
                </p>}
            </div>

            {currentQuestion.type === 'contact' ? <div className="space-y-3 md:space-y-4" data-id="element-493">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg border-2 border-white/20 p-4 md:p-6" data-id="element-494">
                  <div className="space-y-3 md:space-y-4" data-id="element-495">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4" data-id="element-496">
                      <div data-id="element-497">
                        <label className="block text-xs md:text-sm font-semibold text-white mb-1.5 md:mb-2" data-id="element-498">
                          First Name
                        </label>
                        <input type="text" value={inputs.firstName || ''} onChange={e => setInputs({
                      ...inputs,
                      firstName: e.target.value
                    })} placeholder="John" className="w-full p-3 md:p-4 rounded-lg border-2 border-white/30 bg-white/5 text-white placeholder-blue-200 outline-none focus:border-amber transition-colors text-base md:text-lg backdrop-blur-sm" data-id="element-499" />
                      </div>
                      <div data-id="element-500">
                        <label className="block text-xs md:text-sm font-semibold text-white mb-1.5 md:mb-2" data-id="element-501">
                          Last Name
                        </label>
                        <input type="text" value={inputs.lastName || ''} onChange={e => setInputs({
                      ...inputs,
                      lastName: e.target.value
                    })} placeholder="Smith" className="w-full p-3 md:p-4 rounded-lg border-2 border-white/30 bg-white/5 text-white placeholder-blue-200 outline-none focus:border-amber transition-colors text-base md:text-lg backdrop-blur-sm" data-id="element-502" />
                      </div>
                    </div>
                    <div data-id="element-503">
                      <label className="block text-xs md:text-sm font-semibold text-white mb-1.5 md:mb-2" data-id="element-504">
                        Company Name
                      </label>
                      <input type="text" value={inputs.companyName} onChange={e => setInputs({
                    ...inputs,
                    companyName: e.target.value
                  })} placeholder="Your Company" className="w-full p-3 md:p-4 rounded-lg border-2 border-white/30 bg-white/5 text-white placeholder-blue-200 outline-none focus:border-amber transition-colors text-base md:text-lg backdrop-blur-sm" data-id="element-505" />
                    </div>
                    <div data-id="element-506">
                      <label className="block text-xs md:text-sm font-semibold text-white mb-1.5 md:mb-2" data-id="element-507">
                        Work Email
                      </label>
                      <input type="email" value={inputs.email} onChange={e => setInputs({
                    ...inputs,
                    email: e.target.value
                  })} placeholder="you@company.com" className="w-full p-3 md:p-4 rounded-lg border-2 border-white/30 bg-white/5 text-white placeholder-blue-200 outline-none focus:border-amber transition-colors text-base md:text-lg backdrop-blur-sm" data-id="element-508" />
                    </div>
                    <div data-id="element-509">
                      <label className="block text-xs md:text-sm font-semibold text-white mb-1.5 md:mb-2" data-id="element-510">
                        Phone Number
                      </label>
                      <input type="tel" value={inputs.phone || ''} onChange={e => setInputs({
                    ...inputs,
                    phone: e.target.value
                  })} placeholder="(555) 123-4567" className="w-full p-3 md:p-4 rounded-lg border-2 border-white/30 bg-white/5 text-white placeholder-blue-200 outline-none focus:border-amber transition-colors text-base md:text-lg backdrop-blur-sm" data-id="element-511" />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3" data-id="element-512">
                  <button onClick={() => handleAnswer('email', inputs.email)} disabled={!inputs.email || !inputs.companyName || !inputs.firstName || !inputs.lastName || !inputs.phone} className="w-full py-4 md:py-5 bg-amber hover:bg-amber-dark disabled:bg-amber/50 disabled:cursor-not-allowed text-navy font-bold text-lg md:text-xl rounded-lg transition-all shadow-xl hover:scale-105 active:scale-100 disabled:hover:scale-100" data-id="element-513">
                    Continue with Contact Info →
                  </button>

                  <button onClick={handleSkipToResults} className="w-full py-3 md:py-4 bg-white/10 hover:bg-white/20 border-2 border-white/30 hover:border-white/50 text-white font-semibold text-base md:text-lg rounded-lg transition-all backdrop-blur-sm" data-id="element-514">
                    Skip to Results
                  </button>
                </div>
              </div> : currentQuestion.type === 'range' ? <div className="bg-white/10 backdrop-blur-sm rounded-lg border-2 border-white/20 p-6 md:p-8" data-id="element-515">
                <div className="mb-6" data-id="element-516">
                  <div className="flex items-center justify-between mb-4" data-id="element-517">
                    <span className="text-blue-200 text-sm font-semibold" data-id="element-518">
                      Your Answer:
                    </span>
                    <span className="text-3xl md:text-4xl font-bold text-amber" data-id="element-519">
                      {currentQuestion.formatValue ? currentQuestion.formatValue(inputs[currentQuestion.field] as number) : inputs[currentQuestion.field]}
                    </span>
                  </div>

                  <input type="range" min={currentQuestion.min} max={currentQuestion.max} step={currentQuestion.step} value={inputs[currentQuestion.field] as number} onChange={e => handleSliderChange(currentQuestion.field, parseFloat(e.target.value))} className="w-full h-3 bg-white/20 rounded-lg appearance-none cursor-pointer" style={{
                background: `linear-gradient(to right, #F59E0B 0%, #F59E0B ${((inputs[currentQuestion.field] as number) - (currentQuestion.min || 0)) / ((currentQuestion.max || 100) - (currentQuestion.min || 0)) * 100}%, rgba(255,255,255,0.2) ${((inputs[currentQuestion.field] as number) - (currentQuestion.min || 0)) / ((currentQuestion.max || 100) - (currentQuestion.min || 0)) * 100}%, rgba(255,255,255,0.2) 100%)`
              }} data-id="element-520" />

                  <div className="flex justify-between text-xs text-blue-200 mt-2" data-id="element-521">
                    <span data-id="element-522">
                      {currentQuestion.formatValue ? currentQuestion.formatValue(currentQuestion.min || 0) : currentQuestion.min}
                    </span>
                    <span data-id="element-523">
                      {currentQuestion.formatValue ? currentQuestion.formatValue(currentQuestion.max || 100) : currentQuestion.max}
                    </span>
                  </div>
                </div>

                <button onClick={() => handleAnswer(currentQuestion.field, inputs[currentQuestion.field])} className="w-full py-4 md:py-5 bg-amber hover:bg-amber-dark text-navy font-bold text-lg md:text-xl rounded-lg transition-all shadow-xl hover:scale-105 active:scale-100" data-id="element-524">
                  Continue →
                </button>
              </div> : null}
          </div>
        </div>
      </div>

      <footer className="bg-navy/50 border-t-2 border-white/10" data-id="element-525">
        <div className="max-w-7xl mx-auto px-4 py-4 md:py-6" data-id="element-526">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3" data-id="element-527">
            <img src="https://cdn.magicpatterns.com/uploads/jrZpZA6KS7cefrnJNRcqNs/Copy_of_HIRE.png" alt="HIRE" className="h-6 md:h-8" data-id="element-528" />
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-xs md:text-sm text-blue-200" data-id="element-529">
              <button onClick={() => navigate('/')} className="hover:text-white transition-colors" data-id="element-530">
                Home
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}